import { useEffect, useRef, useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import type { Role } from "@contractorhub/shared";
import { apiFetch, errorMessage } from "../lib/api";
import { homeFor } from "../lib/auth";
import {
  currentAccount,
  handledAtBootstrap,
  isConfigured,
  login as msalLogin,
} from "../lib/msal";
import { devActivate } from "../lib/dev-auth";
import { queryKeys } from "../lib/queries";
import { Alert } from "../components/ui";

const searchSchema = z.object({ token: z.string().optional() });

/**
 * The invite landing page (S2.2). It carries the invite token through the Entra
 * sign-in `state` so activation binds to *this* invite, not to whatever address
 * the contractor types. When we return already signed in (e.g. the same session),
 * we activate directly here; the fresh-redirect case is handled at bootstrap.
 */
export const Route = createFileRoute("/activate")({
  validateSearch: searchSchema,
  component: ActivatePage,
});

function ActivatePage() {
  const { token } = Route.useSearch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    void (async () => {
      if (!token) {
        setError("This invitation link is missing its token.");
        return;
      }

      // Back from sign-in: bootstrap has already activated this token. Activating
      // again is refused (the invite is ACCEPTED now) and would show a working
      // account an error. On success it cached the user; on failure it is already
      // on its way to /login with the reason.
      if (handledAtBootstrap(token)) {
        const me = queryClient.getQueryData<{ user: { role: Role } | null }>(
          queryKeys.me
        );
        if (me?.user) await router.navigate({ to: homeFor(me.user.role) });
        return;
      }

      // Dev mode (no MSAL): activate the dev way — the API takes the identity
      // from the invite itself — then sign in as the new contractor.
      if (import.meta.env.DEV && !isConfigured()) {
        try {
          const user = await devActivate(token);
          queryClient.setQueryData(queryKeys.me, { user });
          await router.navigate({ to: homeFor(user.role) });
        } catch (err) {
          setError(errorMessage(err));
        }
        return;
      }

      // Already signed in? Activate straight away. Otherwise start sign-in with
      // the token in `state`; bootstrap completes activation on the way back.
      if (currentAccount()) {
        try {
          const { user } = await apiFetch("POST /api/invite/activate", {
            body: { token },
          });
          queryClient.setQueryData(queryKeys.me, { user });
          await router.navigate({ to: homeFor(user.role) });
        } catch (err) {
          setError(errorMessage(err));
        }
      } else {
        try {
          await msalLogin(token);
        } catch {
          setError("Couldn't start sign-in. Please try the link again.");
        }
      }
    })();
  }, [token, router, queryClient]);

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-sm text-center">
        {error ? (
          <>
            <h1 className="text-xl font-semibold mb-3">Activation problem</h1>
            <Alert tone="error">{error}</Alert>
            <a href="/login" className="mt-4 inline-block text-sm text-accent underline">
              Go to sign in
            </a>
          </>
        ) : (
          <>
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-accent" />
            <p className="mt-3 text-sm text-subtle">Activating your account…</p>
          </>
        )}
      </div>
    </div>
  );
}
